import { Box, Flex, FormErrorMessage, Stack, Text } from '@chakra-ui/core'
import { Controller, useFormContext } from 'react-hook-form'
import { Link2 } from 'lucide-react'
import React, { useEffect } from 'react'

import { POP_REFERENCE_TYPE } from 'constants/enums'
import { IPopReferenceParams } from 'constants/interfaces'
import { PopFormValues, PopType } from 'components/PopCreation/types'
import { PopRecipients } from '../fileds/PopRecipients'
import { TitleField } from '../fileds/TitleField'
import { BodyField } from '../fileds/BodyField'
import { BtnTextField } from '../fileds/BtnTextField'
import { UploadMediaSection } from '../fileds/UploadMediaSection'
import { PushTitleField } from '../fileds/PushTitleField'
import { PopSwitch } from '../fileds/PopSwitch'
import { ProfileImageField } from '../fileds/ProfileImageField'
import { PushMessageField } from '../fileds/PushMessageField'
import { SearchableMenu } from 'components/AdminConsole/commonUI/SearchableMenu'
import { StyledFormControl, StyledFormLabel } from 'components/AdminConsole/commonUI/styles'
import useLocaleText from 'components/useLocaleText'
import keys from 'constants/keys'
import { getNestedError } from '../utils'

const ACTION_POP_REFERENCE_TYPES = [
  {
    id: POP_REFERENCE_TYPE.TASKS.toString(),
    name: 'tasks',
    isShowId: false,
  },
  {
    id: POP_REFERENCE_TYPE.PRODUCT_PERFORMANCE.toString(),
    name: 'product_performance',
    isShowId: false,
  },
]

const TASK_VIEW_OPTIONS = Object.values(keys.DEFAULT_TASK_VIEWS).map((id) => ({
  id: id.toString(),
  name: `create_task_view_op_${id}`,
  isShowId: false,
}))

export const ActionPopForm = () => {
  const { control, setValue, watch, clearError, errors } = useFormContext<PopFormValues>()
  const pick_audience = useLocaleText('pick_audience')
  const enable_push_notification = useLocaleText('enable_push_notification', true)
  const push_title = useLocaleText('push_title', true)
  const push_body = useLocaleText('push_body', true)
  const push_title_placeholder = useLocaleText('push_title_placeholder', true)
  const push_body_placeholder = useLocaleText('push_body_placeholder', true)
  const reference_type = useLocaleText('reference_type', true)
  const reference_type_placeholder = useLocaleText('reference_type_placeholder', true)
  const error_reference_type = useLocaleText('error_reference_type', true)
  const reference_view = useLocaleText('view', true)
  const reference_view_placeholder = useLocaleText('reference_view_placeholder', true)
  const error_reference_view = useLocaleText('error_reference_view', true)

  const selectedReferenceType = watch('data.reference_type') as number | undefined
  const selectedReferenceParams = watch('data.reference_params') as IPopReferenceParams | undefined
  const selectedView = selectedReferenceParams?.view
  const isTasksType = selectedReferenceType === POP_REFERENCE_TYPE.TASKS
  const referenceTypeError = getNestedError(errors, 'data.reference_type')
  const referenceViewError = getNestedError(errors, 'data.reference_params')

  useEffect(() => {
    if (selectedReferenceType === undefined || selectedReferenceType === null) {
      setValue('data.reference_type', POP_REFERENCE_TYPE.PRODUCT_PERFORMANCE)
      setValue('data.reference_params', {})
    }
  }, [selectedReferenceType, setValue])

  return (
    <Stack style={{ gap: '15px' }}>
      <Stack style={{ gap: '10px' }} mt={4}>
        <ProfileImageField />
        <TitleField />
        <BodyField keyName="data.pop_body" label="pop_body" placeholder="pop_body_placeholder" />
        <BtnTextField keyName="data.ack_btn_text" label="ack_btn" placeholder="ack_btn_placeholder" />
      </Stack>

      <Flex style={{ gap: '16px' }} flexWrap="wrap">
        <StyledFormControl isInvalid={!!referenceTypeError} w={{ base: '100%', md: '50%' }}>
          <StyledFormLabel required>
            <Link2 size={18} strokeWidth={2.5} />
            <Text>{reference_type}</Text>
          </StyledFormLabel>

          <SearchableMenu
            isInvalid={!!referenceTypeError}
            isShowSearch={false}
            items={ACTION_POP_REFERENCE_TYPES}
            selectedId={(selectedReferenceType ?? POP_REFERENCE_TYPE.PRODUCT_PERFORMANCE).toString()}
            placeholder={reference_type_placeholder}
            isLocalTranslation
            onSelect={(item) => {
              setValue('data.reference_type', Number(item.id), true)
              setValue('data.reference_params', {}, true)
              clearError('data.reference_type')
              clearError('data.reference_params')
            }}
          />

          <FormErrorMessage>{referenceTypeError?.message}</FormErrorMessage>
        </StyledFormControl>

        {isTasksType && (
          <StyledFormControl isInvalid={!!referenceViewError} w={{ base: '100%', md: 'calc(50% - 16px)' }}>
            <StyledFormLabel required>
              <Text>{reference_view}</Text>
            </StyledFormLabel>

            <SearchableMenu
              isInvalid={!!referenceViewError}
              isShowSearch={false}
              items={TASK_VIEW_OPTIONS}
              selectedId={selectedView !== undefined ? selectedView.toString() : ''}
              placeholder={reference_view_placeholder}
              isLocalTranslation
              onSelect={(item) => {
                setValue('data.reference_params', { view: Number(item.id) }, true)
                clearError('data.reference_params')
              }}
            />

            <FormErrorMessage>{referenceViewError?.message}</FormErrorMessage>
          </StyledFormControl>
        )}
      </Flex>

      <Box>
        <Text fontSize="14px" fontWeight="bold" color="gray.500" mb={2}>
          {pick_audience}
        </Text>
        <PopRecipients />
      </Box>

      <UploadMediaSection />

      <Flex flexDir="column" mt={2} w={{ base: '100%', md: 'calc(50% - 10px)' }}>
        <Flex alignItems="center" justifyContent="space-between">
          <Text fontSize="14px" fontWeight="bold" color="gray.500" mb={2} w="100%">
            {enable_push_notification}
          </Text>
          <PopSwitch name="is_send_push_notification" />
        </Flex>

        <PushTitleField
          keyName="data.push_title"
          label={push_title}
          placeholder={push_title_placeholder}
          keySwitch="is_send_push_notification"
        />
        <PushMessageField
          keyName="data.push_body"
          label={push_body}
          placeholder={push_body_placeholder}
          keySwitch="is_send_push_notification"
        />
      </Flex>

      <Controller name="pop_type" control={control} defaultValue={PopType.ActionPop} as={<Box display="none" />} />
      <Controller name="rid" control={control} as={<Box display="none" />} />
      <Controller
        name="data.reference_type"
        control={control}
        defaultValue={POP_REFERENCE_TYPE.PRODUCT_PERFORMANCE}
        rules={{ required: error_reference_type }}
        as={<Box display="none" />}
      />
      <Controller
        name="data.reference_params"
        control={control}
        defaultValue={{}}
        rules={{
          validate: (val: IPopReferenceParams) => {
            if (isTasksType && (val?.view === undefined || val?.view === null)) {
              return error_reference_view
            }
            return true
          },
        }}
        as={<Box display="none" />}
      />
    </Stack>
  )
}
